import { getAllFrames } from '../../../inspector'
import { useCallback, useContext, useMemo } from 'react'
import { ShareVersionContext } from '../../../versioning'
import { GridAndLayoutFrame, GridAndLayoutSetting } from './types'
import { useGridsAndLayoutsContext } from './GridsAndLayoutsContext'
// useInspectorContext needs to be imported like this to avoid circular dependencies
import { useInspectorContext } from '../../FrameView/hooks/inspector'

const DEFAULT_GRID_AND_LAYOUT_SETTING: GridAndLayoutSetting = {
  isGridEnabled: false,
  isLayoutEnabled: false,
}

// MARK: Exported hooks

/**
 * Returns the list of frames in the current document that have a grid or layout settings enabled.
 * This is the list of frames for which we need to render the grid and layout overlays.
 */
export function useFramesWithGridAndLayout(): GridAndLayoutFrame[] {
  const gridsAndLayoutsForDoc = useGridsAndLayoutsForDocument()
  const gridAndLayoutByFrame = gridsAndLayoutsForDoc?.gridAndLayoutByFrame

  const hasFramesWithGridAndLayout =
    gridAndLayoutByFrame && Object.keys(gridAndLayoutByFrame).length > 0

  const allFrames = useAllFrames(hasFramesWithGridAndLayout)

  const framesWithGridAndLayout = useMemo(() => {
    if (!hasFramesWithGridAndLayout) {
      return []
    }

    return allFrames.reduce((acc: GridAndLayoutFrame[], frame) => {
      const gridAndLayoutForFrame = gridAndLayoutByFrame[frame.elementUUID]
      if (gridAndLayoutForFrame) {
        acc.push({
          frameId: frame.elementUUID,
          isGridEnabled: gridAndLayoutForFrame.isGridEnabled,
          isLayoutEnabled: gridAndLayoutForFrame.isLayoutEnabled,
          frameElement: frame,
        })
      }

      return acc
    }, [])
  }, [allFrames, gridAndLayoutByFrame, hasFramesWithGridAndLayout])

  return framesWithGridAndLayout
}

/**
 * Returns the grid and layout settings for a specific frame in the current document.
 */
export function useGridAndLayoutSettingsForFrame(frameId: string | undefined) {
  const gridsAndLayoutsForDoc = useGridsAndLayoutsForDocument()
  const frameSettings = frameId
    ? gridsAndLayoutsForDoc?.gridAndLayoutByFrame?.[frameId]
    : undefined

  return frameSettings ?? DEFAULT_GRID_AND_LAYOUT_SETTING
}

/**
 * Allow to toggle the isGridEnabled and isLayoutEnabled settings for a specific frame.
 */
export function useToggleGridSettingsForFrame(frameId: string | undefined) {
  const setFrameGridAndLayoutSetting = useSetFrameGridAndLayoutSetting()

  const toggleGrid = useCallback(() => {
    if (frameId === undefined || !setFrameGridAndLayoutSetting) return

    setFrameGridAndLayoutSetting(frameId, prev => ({
      ...prev,
      isGridEnabled: !prev.isGridEnabled,
    }))
  }, [frameId, setFrameGridAndLayoutSetting])

  const toggleLayout = useCallback(() => {
    if (frameId === undefined || !setFrameGridAndLayoutSetting) return

    setFrameGridAndLayoutSetting(frameId, prev => ({
      ...prev,
      isLayoutEnabled: !prev.isLayoutEnabled,
    }))
  }, [frameId, setFrameGridAndLayoutSetting])

  return useMemo(() => {
    return {
      toggleGrid,
      toggleLayout,
    }
  }, [toggleGrid, toggleLayout])
}

// MARK: Internal

/**
 * Returns the grid and layout settings data for the current document.
 */
function useGridsAndLayoutsForDocument() {
  const { gridsAndLayoutsByDocument } = useGridsAndLayoutsContext()

  const shareVersionContext = useContext(ShareVersionContext)

  if (!shareVersionContext) {
    // @TODO: default value for previews
    return undefined
  }

  const { share } = shareVersionContext

  const gridsAndLayoutsForDoc = gridsAndLayoutsByDocument?.[share.identifier]

  return gridsAndLayoutsForDoc
}

/**
 * Set the grid and layout settings for a specific frame of the current document.
 */
function useSetFrameGridAndLayoutSetting() {
  const { gridsAndLayoutsByDocument, setGridsAndLayoutsByDocument } =
    useGridsAndLayoutsContext()

  const shareVersionContext = useContext(ShareVersionContext)
  const { share } = shareVersionContext || {}

  // Call useCallback unconditionally at the top level
  const setFrameGridAndLayoutSetting = useCallback(
    (
      frameId: string,
      getNewGridAndLayoutSettingForFrame: (
        prev: GridAndLayoutSetting
      ) => GridAndLayoutSetting
    ) => {
      // Early return if no share context
      if (!share) return

      // useLocalStorage from react-use (used in GridsAndLayoutsProvider.tsx) has a bug that prevents us from passing a function
      // to update the state from the previous state. So we do it manually as a workaround.
      // https://github.com/streamich/react-use/issues/2560
      const prev = gridsAndLayoutsByDocument

      const prevGridAndLayoutByFrame =
        prev?.[share.identifier]?.gridAndLayoutByFrame
      const prevSettingsForFrame =
        prevGridAndLayoutByFrame?.[frameId] ?? DEFAULT_GRID_AND_LAYOUT_SETTING

      const newSettingForFrame =
        getNewGridAndLayoutSettingForFrame(prevSettingsForFrame)

      const newGridAndLayoutByFrame: Record<string, GridAndLayoutSetting> = {
        ...prevGridAndLayoutByFrame,
      }

      const isDefaultSetting =
        newSettingForFrame.isGridEnabled === false &&
        newSettingForFrame.isLayoutEnabled === false

      if (isDefaultSetting) {
        // If both grid and layout are disabled, we are back to the default and
        // we can remove the frame from the settings
        delete newGridAndLayoutByFrame[frameId]
      } else {
        // Otherwise, we update the settings for the frame
        newGridAndLayoutByFrame[frameId] = newSettingForFrame
      }

      const newState = { ...prev }

      if (Object.keys(newGridAndLayoutByFrame).length === 0) {
        // If there are no frames with grid and layout settings, we can remove the document entry
        delete newState[share.identifier]
      } else {
        // Otherwise, we update the document entry with the new settings
        newState[share.identifier] = {
          gridAndLayoutByFrame: newGridAndLayoutByFrame,
          lastUpdated: Date.now(),
        }
      }

      setGridsAndLayoutsByDocument(newState)
    },
    [gridsAndLayoutsByDocument, setGridsAndLayoutsByDocument, share]
  )

  if (!shareVersionContext) {
    // @TODO: default value for previews
    return () => {}
  }

  return setFrameGridAndLayoutSetting
}

/**
 * Returns all frames (top or nested) in the current sketch scene.
 * The enabled parameter allows to disable the processing of frames until it is needed.
 * If this process ends up taking a toll on performance, we can consider moving it
 * to be part of the tree creation function buildSketchSceneTreeFromPRFile.
 */
function useAllFrames(enabled: boolean = true) {
  const { sketchSceneRootElement } = useInspectorContext()

  return useMemo(() => {
    if (!sketchSceneRootElement || !enabled) {
      return []
    }
    return getAllFrames(sketchSceneRootElement)
  }, [enabled, sketchSceneRootElement])
}
